<template>
  <v-container id="set_tickets">
    <h3
      v-html="t('set_tickets.title')"
      class="text-body-1 text-sm-h6 font-weight-bold"
    ></h3>

    <v-row class="mt-2 justify-center">
      <v-col
        v-for="(item, index) in visibleItems"
        :key="index"
        cols="12"
        class="card-col py-5"
      >
        <v-card
          :id="item.id"
          max-width="100%"
          elevation="0"
          class="mx-auto set-ticket-card"
        >
          <div class="card-content">
            <v-img
              :aspect-ratio="15.9235 / 10"
              cover
              min-height="200px"
              :src="item.fullImagePath"
              class="set-ticket-image"
              crossorigin="anonymous"
            >
              <div v-if="item?.label" class="card-label">{{ item.label }}</div>
              <div class="image_notice" v-html="item.image_notice"></div>
            </v-img>
            <div class="ticket-details">
              <p
                class="text-body-1 text-sm-h6 font-weight-bold text-left text-pre-wrap"
                v-html="item.title"
              ></p>
              <p
                v-if="!isMobileDevice"
                class="text-body-2 details-caption mt-4 text-pre-wrap"
                align="left"
                :style="getCaptionStyle(item.caption_limit)"
                v-html="item.caption"
              ></p>
              <p
                class="text-body-1 text-right mt-7 text-pre-wrap"
                align="left"
                v-html="item.price"
              ></p>
            </div>
            <div class="button-container">
              <v-btn
                class="agency-button"
                rounded="xl"
                size="large"
                variant="outlined"
                elevation="7"
                :disabled="isEventEnded || item.buttonStatus === 'sold_out'"
                @click="openModal(item)"
              >
                {{ getButtonText(item.buttonStatus) }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- モーダル表示 -->
    <v-dialog v-model="isModalOpen" class="modal-card">
      <v-btn
        @click="closeModal"
        class="close-btn"
        icon="$close"
        size="large"
        variant="text"
      ></v-btn>
      <v-card>
        <v-img :src="selectedCard?.fullImagePath" cover crossorigin="anonymous">
          <div class="image_notice" v-if="selectedCard?.image_notice" v-html="selectedCard.image_notice"></div>
        </v-img>
        <v-card-item class="card-body py-7">
          <p v-if="selectedCard?.label" class="modal-label">
            {{ selectedCard.label }}
          </p>
          <p v-if="selectedCard?.subtitle" class="modal-subtitle">
            {{ selectedCard.subtitle }}
          </p>
          <h3 v-if="selectedCard?.title"
          v-html="selectedCard.title" class="modal-title text-body-1 text-sm-h6 font-weight-bold text-pre-wrap">
          </h3>
          <p
            v-if="selectedCard?.caption"
            v-html="selectedCard.caption"
            class="modal-description text-body-2 text-sm-body-1 mt-2 text-pre-wrap"
          ></p>
          <p
            v-if="selectedCard?.copy"
            v-html="selectedCard.copy"
            class="modal-description text-center text-body-2 text-sm-body-1 mt-7 text-pre-wrap"
          ></p>
          <p
            v-if="selectedCard?.price"
            v-html="selectedCard.price"
            class="modal-description text-body-2 text-sm-body-1 font-weight-bold text-right mt-2 text-pre-wrap"
          ></p>

          <!-- テーブル表示 -->
          <div class="subway-tickets-table">
            <v-table
              v-if="selectedCard?.table"
              class="ticket-table text-caption text-center mt-7"
              style="white-space: nowrap"
            >
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="blank text-center"></th>
                    <th
                      v-html="selectedCard.table.group1"
                      class="text-center text-body-2"
                    ></th>
                    <th
                      v-html="selectedCard.table.group2"
                      class="text-center text-body-2"
                    ></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(day, dayIndex) in selectedCard.table.days"
                    :key="dayIndex"
                  >
                    <td v-html="day.title"></td>
                    <td><span v-html="day.group1" class="text-body-2"></span></td>
                    <td><span v-html="day.group2" class="text-body-2"></span></td>
                  </tr>
                </tbody>
              </template>
            </v-table>
          </div>

          <div
            class="button-container my-7"
            v-if="selectedCard && selectedCard.links"
          >
            <v-row class="" justify="center">
              <v-col
                v-for="(link, linkIndex) in selectedCard.links"
                :key="linkIndex"
                cols="auto"
              >
                <v-btn
                  class="agency-button"
                  rounded="xl"
                  size="large"
                  variant="outlined"
                  elevation="0"
                  min-width="120px"
                  style="text-transform: none"
                  :href="link.link"
                  target="_blank"
                  :disabled="isEventEnded || link.disabled"
                  :class="link.class"
                >
                  {{ (isEventEnded || link.disabled) ? t("set_tickets.sold_out") : link.title }}
                  <SvgIcon v-if="!isEventEnded && !link.disabled" type="mdi" :path="mdiOpenInNew" size="16" />
                </v-btn>
              </v-col>
            </v-row>
          </div>
          <p
            v-if="selectedCard?.notice"
            v-html="selectedCard.notice"
            class="modal-description text-body-2 text-sm-body-1 text-not text-left mt-2"
          ></p>
        </v-card-item>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useIsMobile } from "@/composables/isMobile";
import { mdiOpenInNew } from "@mdi/js";
import SvgIcon from "@jamescoyle/vue-icon";
import localeData, { setOrder, imagePathsMapping, IMAGE_VERSION } from "@/locales/SetTickets";
import { getBasePath } from '@/utils/basePath';
import { isEventEnded } from "@/config/eventStatus";

const { t, locale } = useI18n();
const { isMobileDevice, mdiWindowClose } = useIsMobile();

const basePath = computed(() => getBasePath());

/**
 * ボタンの状態に応じてテキストを取得する関数
 * @param {string} status - ボタンの状態 ('button' または 'sold_out')
 * @returns {string} - 表示するボタンのテキスト
 */
function getButtonText(status) {
  // イベント終了時は常に「販売終了しました」を表示
  if (isEventEnded) {
    return t("set_tickets.sold_out");
  }
  return status === "sold_out"
    ? t("set_tickets.sold_out")
    : t("set_tickets.button");
}

// 画像URLを生成する関数（キャッシュ無効化付き）
const generateImageUrl = (imagePath) => {
  // 本番環境では軽量化
  if (!import.meta.env.DEV) {
    return `${basePath.value}${imagePath}?v=${IMAGE_VERSION}`;
  }
  
  // 開発環境では詳細なキャッシュ無効化
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substr(2, 9);
  const sessionId = sessionStorage.getItem('imageSessionId') || Math.random().toString(36).substr(2, 9);
  if (!sessionStorage.getItem('imageSessionId')) {
    sessionStorage.setItem('imageSessionId', sessionId);
  }
  return `${basePath.value}${imagePath}?v=${IMAGE_VERSION}&cb=${timestamp}&t=${randomId}&s=${sessionId}&nocache=1&dev=true`;
};

const updateLocalizedItems = () => {
  const data = localeData[locale.value]?.set_tickets.items || [];
  const set1 = setOrder['set-1'];
  
  // set-1のcardsの順序に基づいてアイテムをソート
  const sortedItems = set1.cards.map(cardConfig => {
    const item = data.find(item => item.id === cardConfig.id);
    return item ? {
      ...item,
      fullImagePath: generateImageUrl(imagePathsMapping[item.id]),
      expanded: false,
    } : null;
  }).filter(item => item && item.visible);
  
  localizedItems.value = sortedItems;
  
  // 開発環境でのデバッグ情報
  if (import.meta.env.DEV) {
    console.log('🖼️ 画像URL更新:', sortedItems.map(item => ({
      id: item.id,
      path: item.fullImagePath
    })));
  }
};

const localizedItems = ref([]);
updateLocalizedItems();

watch(locale, updateLocalizedItems);

// 画像のプリロード（キャッシュ無効化）
const preloadImages = () => {
  // 本番環境ではプリロードを無効化（パフォーマンス向上）
  if (!import.meta.env.DEV) {
    return;
  }
  
  localizedItems.value.forEach(item => {
    if (item.fullImagePath) {
      const img = new Image();
      img.src = item.fullImagePath;
      img.crossOrigin = 'anonymous';
    }
  });
};

// 開発環境での強制リフレッシュ機能
const forceRefreshImages = () => {
  if (import.meta.env.DEV) {
    // セッションIDをリセット
    sessionStorage.removeItem('imageSessionId');
    // 画像URLを再生成
    updateLocalizedItems();
    // 画像を再プリロード
    setTimeout(() => {
      preloadImages();
    }, 100);
  }
};

// 開発環境でのキーボードショートカット（Ctrl+Shift+R）
const handleKeyDown = (event) => {
  if (import.meta.env.DEV && event.ctrlKey && event.shiftKey && event.key === 'R') {
    event.preventDefault();
    forceRefreshImages();
    console.log('🔄 画像キャッシュを強制リフレッシュしました');
  }
};

// コンポーネントマウント時に画像をプリロード
onMounted(() => {
  preloadImages();
  
  // 開発環境でのキーボードイベントリスナー
  if (import.meta.env.DEV) {
    document.addEventListener('keydown', handleKeyDown);
    console.log('💡 開発モード: Ctrl+Shift+R で画像キャッシュを強制リフレッシュできます');
  }
});

// コンポーネントアンマウント時のクリーンアップ
onUnmounted(() => {
  if (import.meta.env.DEV) {
    document.removeEventListener('keydown', handleKeyDown);
  }
});

const visibleItems = computed(() => {
  return localizedItems.value.filter((item) => item.visible);
});

const isModalOpen = ref(false);
const selectedCard = ref(null);

const openModal = (item) => {
  selectedCard.value = item;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedCard.value = null;
};

// CSSスタイルを生成する関数
const getCaptionStyle = (limit) => {
  // 行数制限がない場合、制御を適用しない
  if (limit === 0) return {};

  // CSSスタイルで行数制限を適用
  return {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    WebkitLineClamp: limit,
    whiteSpace: 'normal',
    lineHeight: '1.5em',
  };
};

</script>

<style lang="scss" scoped>
.set-ticket-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: transparent;
  @media screen and (min-width: 600px) {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
}

.card-col {
  position: relative;
}

.card-col:before {
  position: absolute;
  content: "";
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 97%;
  height: 1px;
  background: rgba(255, 255, 255, 1);
}

.card-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
  @media screen and (min-width: 600px) {
    flex-direction: row;
  }
}

.set-ticket-image {
  width: 100%;
  @media screen and (min-width: 600px) {
    width: 200px;
    max-width: 300px;
    height: auto;
    object-fit: cover;
    overflow: hidden;
  }
}

.ticket-details {
  flex: 1;
  margin: 0 16px;

  .details-caption {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-wrap;
  line-height: 1.2em;
}

  .no-ellipsis {
    -webkit-line-clamp: unset;
    overflow: visible;
    text-overflow: clip;
  }
}

.button-container {
  display: flex;
  justify-content: center;
  @media screen and (min-width: 600px) {
    justify-content: flex-end;
    align-items: center;
  }
}

.image_notice {
  position: absolute;
  bottom: 5px;
  right: 5px;
  font-size: 12px;
}

.modal-card {
  max-width: 600px;
  @media screen and (min-width: 1200px) {
    max-width: 800px;
  }

  .card-body {
    background: rgb(var(--v-theme-background), 1);
  }
}

.close-btn {
  position: absolute;
  right: 0;
  top: -50px;
  font-size: 20px;
  color: white;
}

.card-label {
  position: absolute;
  left: 10px;
  top: 10px;
  background: rgb(var(--v-theme-primary), 1);
  font-weight: bold;
  display: inline-block;
  padding: 0.3em 0.5em 0.4em;
  font-size: 12px;
  border-radius: 4px;
}

.modal-label {
  background: rgb(var(--v-theme-primary), 1);
  font-weight: bold;
  display: inline-block;
  padding: 0.2em 0.3em 0.2em;
  font-size: 12px;
  border-radius: 4px;
  margin-bottom: 4px;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  padding: 0;
  list-style: none;
  &::before {
    content: none;
  }
}

.modal-subtitle {
  color: white;
  font-size: 12px;
  margin: 0;
  padding: 0;
}

/* モーダルの背景 */
:deep(.v-overlay__scrim) {
  background: rgb(var(--v-theme-note), 1);
  opacity: 0.7;
}
/* モーダルの高さ設定 */
:deep(.v-overlay__content) {
  max-height: calc(100% - 100px) !important; //上下50pxずつ余白を取る
}


.subway-tickets-table {
  overflow-x: auto;
  padding-bottom: 1rem;
}

:deep(.v-table.ticket-table) {
  border-collapse: collapse;
  border-color: white;
  width: 99%;

  th,
  td {
    padding: 12px 0;
    text-align: center;
    border: 1px solid white !important;
  }
  th.blank {
    background-color: rgba(var(--v-theme-note), 1)!important;
  }

  tr td:first-child {
    background-color: rgba(var(--v-theme-third), 1);
  }
  thead tr:first-child th:nth-child(n + 2) {
    background-color: rgba(var(--v-theme-secondary), 1);
  }
}
.tickets {
  &-before-note {
    color: rgba(var(--v-theme-note), 1);
  }
  &-after-note {
    color: rgba(var(--v-theme-secondary), 1);
  }
}


.display-none {
  display: none;
}
</style>
